import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck,
  Users as UsersIcon,
  MessageSquare,
  Plus,
  Trash2,
  Pencil,
  X,
  Search,
  Mail,
  RefreshCcw,
  BarChart3,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const emptyUserDraft = () => ({
  id: null,
  email: "",
  name: "",
  password: "",
  plan: "free",
});

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function Stat({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-neutral-200 bg-white p-5"
    >
      <p className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</p>
      <p className="mt-2 font-serif text-4xl font-semibold tracking-tight">{value}</p>
    </div>
  );
}

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("users"); // "users" | "messages"

  // Users
  const [users, setUsers] = useState([]);
  const [userStats, setUserStats] = useState({ total: 0, pro: 0, free: 0 });
  const [usersLoading, setUsersLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("all"); // all | free | pro
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("new"); // new | edit
  const [draft, setDraft] = useState(emptyUserDraft());
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Messages
  const [messages, setMessages] = useState([]);
  const [msgTotal, setMsgTotal] = useState(0);
  const [messagesLoading, setMessagesLoading] = useState(false);

  // Access gate — memoize before any conditional return
  const isAdmin = !!user?.is_admin;

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: { limit: 500 } });
      setUsers(data.items || []);
      setUserStats({ total: data.total || 0, pro: data.pro || 0, free: data.free || 0 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  };

  const loadMessages = async () => {
    setMessagesLoading(true);
    try {
      const { data } = await api.get("/admin/contact-messages", { params: { limit: 500 } });
      setMessages(data.items || []);
      setMsgTotal(data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load messages");
    } finally {
      setMessagesLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    loadUsers();
    // Fetch messages count so the stat card is accurate before the tab is opened
    loadMessages();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    if (tab === "messages" && messages.length === 0 && !messagesLoading) {
      loadMessages();
    }
  }, [tab, isAdmin]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (planFilter !== "all" && u.plan !== planFilter) return false;
      if (!q) return true;
      return (
        (u.email || "").toLowerCase().includes(q) ||
        (u.name || "").toLowerCase().includes(q) ||
        (u.user_id || "").toLowerCase().includes(q)
      );
    });
  }, [users, search, planFilter]);

  // Access gates
  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA]">
        <div
          data-testid="admin-loading"
          className="h-6 w-6 animate-spin rounded-full border-2 border-neutral-300 border-t-black"
        />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Navbar />
        <main className="mx-auto max-w-3xl px-6 py-24 text-center lg:px-10">
          <ShieldCheck className="mx-auto mb-4 h-8 w-8 text-neutral-400" strokeWidth={1.5} />
          <h1 data-testid="admin-forbidden" className="font-serif text-4xl font-bold">
            Admin access only
          </h1>
          <p className="mt-3 text-sm text-neutral-600">
            You don&apos;t have permission to view this page.
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  const openNew = () => {
    setDraft(emptyUserDraft());
    setEditorMode("new");
    setEditorOpen(true);
  };

  const openEdit = (u) => {
    setDraft({
      id: u.user_id,
      email: u.email,
      name: u.name || "",
      password: "",
      plan: u.plan || "free",
    });
    setEditorMode("edit");
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setDraft(emptyUserDraft());
  };

  const canSave = (() => {
    if (!draft.name.trim()) return false;
    if (editorMode === "new") {
      if (!draft.email.trim()) return false;
      if (!draft.password || draft.password.length < 6) return false;
    } else if (draft.password && draft.password.length < 6) {
      return false;
    }
    return !saving;
  })();

  const handleSave = async () => {
    if (!canSave) {
      toast.error("Fill in required fields (password must be 6+ chars)");
      return;
    }
    setSaving(true);
    try {
      if (editorMode === "new") {
        await api.post("/admin/users", {
          email: draft.email.trim().toLowerCase(),
          name: draft.name.trim(),
          password: draft.password,
          plan: draft.plan,
        });
        toast.success(`User "${draft.email}" created`);
      } else {
        const payload = {
          name: draft.name.trim(),
          plan: draft.plan,
        };
        if (draft.password) payload.password = draft.password;
        await api.patch(`/admin/users/${draft.id}`, payload);
        toast.success("User updated");
      }
      closeEditor();
      await loadUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (u) => {
    if (u.user_id === user.user_id) {
      toast.error("You can't delete your own admin account");
      return;
    }
    if (!window.confirm(`Delete user ${u.email}? All their content will be removed. This cannot be undone.`)) {
      return;
    }
    setDeletingId(u.user_id);
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("User deleted");
      await loadUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 py-12 lg:px-10">
        {/* Header */}
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-neutral-500">
              <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.5} />
              — Admin
            </p>
            <h1
              data-testid="admin-title"
              className="font-serif text-4xl font-bold tracking-tight sm:text-5xl"
            >
              Control room
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-neutral-600">
              Browse and manage users, review support inquiries, and keep PostShifted running smoothly.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/analytics"
              data-testid="admin-analytics-link"
              className="inline-flex h-11 items-center gap-2 rounded-[3px] border border-neutral-300 bg-white px-4 text-xs text-neutral-700 transition-colors hover:border-black hover:text-black"
            >
              <BarChart3 className="h-4 w-4" strokeWidth={1.5} />
              Analytics
            </Link>
            <Button
              variant="outline"
              onClick={tab === "users" ? loadUsers : loadMessages}
              data-testid="admin-refresh-btn"
              className="h-11 rounded-[3px] border-neutral-300 text-xs"
            >
              <RefreshCcw className="mr-2 h-4 w-4" strokeWidth={1.5} />
              Refresh
            </Button>
            {tab === "users" && (
              <Button
                onClick={openNew}
                data-testid="admin-new-user-btn"
                className="h-11 rounded-[3px] bg-coral px-5 text-white transition-all duration-300 hover:bg-coral-hover hover:shadow-coral"
              >
                <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />
                New user
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="mb-8 grid gap-4 sm:grid-cols-3">
          <Stat label="Total users" value={userStats.total} testid="admin-stat-total" />
          <Stat label="Pro plan" value={userStats.pro} testid="admin-stat-pro" />
          <Stat label="Support messages" value={msgTotal || messages.length} testid="admin-stat-messages" />
        </div>

        {/* Tabs */}
        <div className="mb-6 flex items-center gap-1 border-b border-neutral-200">
          <button
            type="button"
            onClick={() => setTab("users")}
            data-testid="admin-tab-users"
            className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-3 text-sm transition-colors ${
              tab === "users"
                ? "border-coral text-neutral-900"
                : "border-transparent text-neutral-500 hover:text-neutral-900"
            }`}
          >
            <UsersIcon className="h-4 w-4" strokeWidth={1.5} />
            Users
          </button>
          <button
            type="button"
            onClick={() => setTab("messages")}
            data-testid="admin-tab-messages"
            className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-3 text-sm transition-colors ${
              tab === "messages"
                ? "border-coral text-neutral-900"
                : "border-transparent text-neutral-500 hover:text-neutral-900"
            }`}
          >
            <MessageSquare className="h-4 w-4" strokeWidth={1.5} />
            Support messages
          </button>
        </div>

        {/* Users tab */}
        {tab === "users" && (
          <section>
            {/* Filters */}
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[240px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" strokeWidth={1.5} />
                <Input
                  data-testid="admin-users-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by email, name, or ID"
                  className="h-11 rounded-none border-neutral-300 pl-10 focus-visible:ring-1 focus-visible:ring-black"
                />
              </div>
              <div className="flex items-center gap-1 border border-neutral-300 bg-white p-1">
                {["all", "free", "pro"].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPlanFilter(p)}
                    data-testid={`admin-plan-filter-${p}`}
                    className={`px-3 py-1.5 text-xs uppercase tracking-widest transition-colors ${
                      planFilter === p ? "bg-black text-white" : "text-neutral-600 hover:text-black"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto border border-neutral-200 bg-white">
              <table className="w-full text-sm" data-testid="admin-users-table">
                <thead>
                  <tr className="border-b border-neutral-200 bg-neutral-50 text-left text-[10px] uppercase tracking-widest text-neutral-500">
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Plan</th>
                    <th className="px-4 py-3">Provider</th>
                    <th className="px-4 py-3">Onboarding</th>
                    <th className="px-4 py-3">Created</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {usersLoading ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-16 text-center">
                        <div
                          data-testid="admin-users-loading"
                          className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-neutral-300 border-t-black"
                        />
                      </td>
                    </tr>
                  ) : filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-16 text-center text-sm text-neutral-500" data-testid="admin-users-empty">
                        No users match your filters.
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((u) => (
                      <tr
                        key={u.user_id}
                        data-testid={`admin-user-row-${u.user_id}`}
                        className="border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50"
                      >
                        <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                        <td className="px-4 py-3">{u.name || "—"}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block border px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                              u.plan === "pro"
                                ? "border-coral bg-coral-subtle text-coral"
                                : "border-neutral-300 bg-neutral-50 text-neutral-600"
                            }`}
                          >
                            {u.plan || "free"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-neutral-600">{u.provider || "email"}</td>
                        <td className="px-4 py-3 text-xs text-neutral-600">{u.onboarding_step || "—"}</td>
                        <td className="px-4 py-3 text-xs text-neutral-600">{formatDate(u.created_at)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => openEdit(u)}
                              data-testid={`admin-user-edit-${u.user_id}`}
                              className="p-2 text-neutral-500 hover:text-black"
                              aria-label="Edit user"
                            >
                              <Pencil className="h-4 w-4" strokeWidth={1.5} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(u)}
                              disabled={deletingId === u.user_id || u.user_id === user.user_id}
                              data-testid={`admin-user-delete-${u.user_id}`}
                              className="p-2 text-neutral-500 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-30"
                              aria-label="Delete user"
                            >
                              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Messages tab */}
        {tab === "messages" && (
          <section data-testid="admin-messages-section">
            {messagesLoading ? (
              <div className="flex justify-center py-24" data-testid="admin-messages-loading">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-neutral-300 border-t-black" />
              </div>
            ) : messages.length === 0 ? (
              <div className="border border-dashed border-neutral-300 bg-white p-16 text-center" data-testid="admin-messages-empty">
                <Mail className="mx-auto mb-4 h-8 w-8 text-neutral-400" strokeWidth={1.5} />
                <p className="font-serif text-2xl italic text-neutral-500">Inbox zero.</p>
                <p className="mt-2 text-sm text-neutral-500">No support messages yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((m) => (
                  <div
                    key={m.id}
                    data-testid={`admin-message-${m.id}`}
                    className="border border-neutral-200 bg-white p-5"
                  >
                    <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="font-serif text-lg font-semibold">
                          {m.name || "Anonymous"}{" "}
                          <span className="font-sans text-xs font-normal text-neutral-500">
                            &lt;{m.email}&gt;
                          </span>
                        </p>
                        {m.topic && (
                          <span className="mt-1 inline-block border border-neutral-300 bg-neutral-50 px-2 py-0.5 text-[10px] uppercase tracking-widest text-neutral-600">
                            {m.topic}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-neutral-500">{formatDate(m.created_at)}</span>
                    </div>
                    <p className="whitespace-pre-wrap text-sm text-neutral-700">{m.message}</p>
                    <a
                      href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.topic || "Your PostShifted inquiry")}`}
                      data-testid={`admin-message-reply-${m.id}`}
                      className="mt-3 inline-flex items-center gap-1 text-xs text-coral underline underline-offset-4 hover:text-coral-hover"
                    >
                      <Mail className="h-3.5 w-3.5" strokeWidth={1.5} />
                      Reply by email
                    </a>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </main>

      {/* User editor modal */}
      {editorOpen && (
        <div
          data-testid="admin-user-editor"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={closeEditor}
        >
          <div
            className="w-full max-w-lg border border-neutral-200 bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6 flex items-center justify-between">
              <p className="text-xs uppercase tracking-[0.25em] text-neutral-500">
                — {editorMode === "edit" ? "Edit user" : "New user"}
              </p>
              <button
                type="button"
                onClick={closeEditor}
                data-testid="admin-editor-close"
                className="text-neutral-400 hover:text-black"
                aria-label="Close editor"
              >
                <X className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500">Email</Label>
                <Input
                  data-testid="admin-editor-email"
                  value={draft.email}
                  onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
                  disabled={editorMode === "edit"}
                  placeholder="user@example.com"
                  className="mt-2 h-11 rounded-none border-neutral-300 focus-visible:ring-1 focus-visible:ring-black disabled:bg-neutral-100"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500">Name</Label>
                <Input
                  data-testid="admin-editor-name"
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value.slice(0, 100) }))}
                  placeholder="Full name"
                  className="mt-2 h-11 rounded-none border-neutral-300 focus-visible:ring-1 focus-visible:ring-black"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500">
                  Password{" "}
                  {editorMode === "edit" && (
                    <span className="text-neutral-400 normal-case tracking-normal">
                      (leave blank to keep current)
                    </span>
                  )}
                </Label>
                <Input
                  data-testid="admin-editor-password"
                  type="text"
                  value={draft.password}
                  onChange={(e) => setDraft((d) => ({ ...d, password: e.target.value.slice(0, 200) }))}
                  placeholder={editorMode === "edit" ? "New password (optional)" : "Min 6 characters"}
                  className="mt-2 h-11 rounded-none border-neutral-300 font-mono text-sm focus-visible:ring-1 focus-visible:ring-black"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500">Plan</Label>
                <div className="mt-2 flex gap-2">
                  {["free", "pro"].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setDraft((d) => ({ ...d, plan: p }))}
                      data-testid={`admin-editor-plan-${p}`}
                      className={`flex-1 border px-4 py-2.5 text-sm uppercase tracking-widest transition-colors ${
                        draft.plan === p
                          ? "border-coral bg-coral-subtle text-coral"
                          : "border-neutral-300 bg-white text-neutral-700 hover:border-black hover:text-black"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={closeEditor}
                data-testid="admin-editor-cancel"
                className="h-11 rounded-none border-neutral-300 text-neutral-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!canSave}
                data-testid="admin-editor-save"
                className="h-11 rounded-[3px] bg-coral px-6 text-white transition-all duration-300 hover:bg-coral-hover hover:shadow-coral disabled:opacity-50"
              >
                {saving ? "Saving…" : editorMode === "edit" ? "Save changes" : "Create user"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
